import { Color, MeshStandardMaterial } from 'three';

import modelLoaderService from './ModelLoaderService';

/**
 * The chest prop, prepared for the board.
 *
 * Same treatment as the pedestal and for the same reason: the GLB ships a rough
 * dielectric, which is the one surface that can pick nothing up off an
 * environment, and this asset is armoured metal whose panel detail is entirely
 * what it reflects. Left as authored it renders as a dark lump at any exposure.
 *
 * What differs from the pedestal is what glows. The chest's teal strips are
 * painted into its base colour with no emissive map, so they can never light up
 * on their own — the same problem the pedestal had. But a chest has no owner and
 * no relation colour, so instead of tinting it per character we drive its own
 * teal back through emission at a fixed hue. The result is that the strips read
 * as powered, which is the whole visual claim of the asset.
 */

export const CHEST_URL = '/models/chest.glb';

/**
 * One full cell.
 *
 * Unlike the pedestal — which is deliberately half a cell so it stays furniture
 * under a figure — a chest IS the thing on that cell, and the brief asks for one
 * cell exactly. Seated by footprint, so how tall the model's proportions make it
 * is the model's business; it measures roughly 0.78 x 1.0 in x/z, so a footprint
 * of 1 puts its long axis across the cell and leaves a little air on the short one.
 */
export const CHEST_FOOTPRINT = 1.0;

const METALNESS = 0.9;
const ROUGHNESS = 0.35;

/** The strip colour, and how hard it is driven back out as light. */
const GLOW_COLOR = 0x39e0d8;
const GLOW_BASE = 0.34;
const GLOW_GAIN = {
  none: 1,
  hover: 1.5,
  selected: 2.0,
  empty: 0,
};

/**
 * How much of the floor's colour the chest takes on.
 *
 * Lower than the pedestal's, on purpose. A plinth is a bright machined plate
 * that should sit inside the map's value range; a chest is a dark armoured box
 * that should read as a solid silhouette against the ground rather than
 * dissolving into it. Enough terrain to belong, not enough to disappear.
 */
const TERRAIN_FLOOR = 0.22;
const TERRAIN_GAIN = 0.9;

/**
 * @returns a holder Group standing on y = 0, one cell wide, centred on x/z.
 *          Its position and scale are free for the caller to set.
 */
export async function buildChest() {
  const model = await modelLoaderService.load(CHEST_URL);
  const holder = modelLoaderService.seat(model, CHEST_FOOTPRINT);

  const lit = [];
  holder.traverse(object => {
    if (!object.isMesh || !object.material) return;
    const source = object.material;
    const material = new MeshStandardMaterial({
      map: source.map || null,
      color: source.color ? source.color.clone() : new Color(0xffffff),
      metalness: METALNESS,
      roughness: ROUGHNESS,
      emissive: new Color(GLOW_COLOR),
      // Driven through the base colour map, so only the parts of the texture
      // that are already bright glow — which is exactly the teal strips. A flat
      // emissive with no map would light the whole box like a lantern.
      emissiveMap: source.map || null,
      emissiveIntensity: GLOW_BASE,
    });
    object.material = material;
    lit.push(material);
  });

  holder.userData.lit = lit;
  return holder;
}

/**
 * Light a built chest for its state and its surroundings.
 *
 * @param group   the holder from `buildChest`
 * @param marker  'none' | 'hover' | 'selected' | 'empty'
 * @param ambient {r,g,b} in 0..1 of the ground it stands on, or null
 */
export function tintChest(group, marker = 'none', ambient = null) {
  const gain = GLOW_GAIN[marker] ?? 1;

  (group.userData.lit || []).forEach(material => {
    material.emissiveIntensity = GLOW_BASE * gain;

    if (ambient) {
      material.color.setRGB(
        TERRAIN_FLOOR + ambient.r * TERRAIN_GAIN,
        TERRAIN_FLOOR + ambient.g * TERRAIN_GAIN,
        TERRAIN_FLOOR + ambient.b * TERRAIN_GAIN
      );
    }
  });
}

export function disposeChestGeometry() {
  modelLoaderService.dispose(CHEST_URL);
}
